import React, { useEffect, useState } from 'react';
import { Star, Trophy, MessageSquare } from 'lucide-react';
import { topRatedItems } from '../../lib/api';
import ReviewsModal from './ReviewsModal';

/**
 * Auto-curated "Top Rated" social-proof strip.
 * Pulls from /api/reviews/top-rated (avg ≥ 4.5 by default, ≥1 review).
 * Renders nothing if there are no qualifying items — keeps the landing page
 * clean during early days.
 */
export default function TopRated() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openItem, setOpenItem] = useState(null);

  useEffect(() => {
    let alive = true;
    topRatedItems(4.5, 8)
      .then((arr) => { if (alive) setItems(Array.isArray(arr) ? arr : []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading || items.length === 0) return null;

  return (
    <section
      id="top-rated"
      className="rounded-xl border border-[color:var(--gold)]/20 bg-gradient-to-br from-[color:var(--gold)]/10 via-zinc-950 to-zinc-950 p-4 md:p-6"
      data-testid="top-rated-section"
    >
      <div className="flex items-end justify-between flex-wrap gap-2 mb-4">
        <div>
          <div className="flex items-center gap-2 text-[color:var(--gold)] mb-1">
            <Trophy size={16} />
            <span className="uppercase tracking-[0.2em] text-[11px] font-bold">Top Rated</span>
          </div>
          <h2 className="text-zinc-100 font-impact text-2xl md:text-3xl tracking-tight">Loved by our users</h2>
          <p className="text-zinc-400 text-xs md:text-sm mt-1">Real ratings from real customers, ranked by average score.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((it) => (
          <button
            key={`${it.item_type}-${it.item_id}`}
            onClick={() => setOpenItem(it)}
            className="relative rounded-xl border border-zinc-800 hover:border-[color:var(--gold)]/40 overflow-hidden text-left transition group"
            style={{ background: it.gradient || 'linear-gradient(180deg, #1a1a1a 0%, #0a0a0a 100%)' }}
            data-testid={`top-rated-${it.item_type}-${it.item_id}`}
          >
            {it.image && (
              <img src={it.image} alt={it.name} className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-70 transition" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            <div className="relative h-32 md:h-36 p-3 flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider text-white/70 bg-black/40 px-2 py-0.5 rounded-full">{it.category || it.item_type}</span>
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/90 backdrop-blur-sm notranslate" translate="no">
                  <Star size={11} className="fill-black text-black" />
                  <span className="text-black text-[11px] font-extrabold">{it.average.toFixed(1)}</span>
                </span>
              </div>
              <div>
                <div className="text-white font-impact text-base md:text-lg leading-tight drop-shadow truncate">{it.name}</div>
                <div className="text-white/70 text-[11px] flex items-center gap-1 mt-0.5">
                  <MessageSquare size={10} /> {it.count} review{it.count === 1 ? '' : 's'}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>

      {openItem && (
        <ReviewsModal
          open={!!openItem}
          onOpenChange={(o) => !o && setOpenItem(null)}
          itemType={openItem.item_type}
          itemId={openItem.item_id}
          itemName={openItem.name}
        />
      )}
    </section>
  );
}
